
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { Button } from "@/components/ui/button";
import { ExternalLink } from "lucide-react";
import { Link } from "react-router-dom";
import { SeoResult } from "../types";

interface RecommendationsTabProps {
  result: SeoResult;
}

export const RecommendationsTab = ({ result }: RecommendationsTabProps) => {
  return (
    <div className="space-y-4">
      <Card>
        <CardHeader>
          <CardTitle>Action Plan</CardTitle>
          <CardDescription>Prioritized recommendations to improve your SEO performance</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-6">
            {result.suggestions.map((suggestion, index) => (
              <div key={index} className="flex items-start gap-3 p-3 border-b last:border-0">
                <div className="bg-desert-sunset/10 text-desert-sunset w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5 font-medium">
                  {index + 1}
                </div>
                <div>
                  <p>{suggestion}</p>
                  <div className="mt-2 flex items-center gap-2">
                    <Badge variant="outline">Priority: {index < 3 ? "High" : index < 6 ? "Medium" : "Low"}</Badge>
                    <Badge variant="outline">Difficulty: {Math.random() > 0.5 ? "Moderate" : Math.random() > 0.5 ? "Easy" : "Advanced"}</Badge>
                    <Badge variant="outline">Impact: {Math.random() > 0.7 ? "High" : Math.random() > 0.5 ? "Medium" : "Low"}</Badge>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
      
      <Card>
        <CardHeader>
          <CardTitle>Next Steps</CardTitle>
          <CardDescription>How to effectively implement these recommendations</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <p>
              Our analysis identified {result.metrics.filter(m => m.status !== "good").length} areas for improvement in your SEO strategy. To achieve better results, we recommend:
            </p>
            <div className="bg-muted/30 p-4 rounded-lg">
              <h4 className="font-medium mb-2">Implementation Approach:</h4>
              <ol className="list-decimal list-inside space-y-2 ml-2">
                <li>Focus on fixing critical issues first (scores below 50)</li>
                <li>Implement technical SEO improvements</li>
                <li>Develop a content strategy targeting identified keyword opportunities</li>
                <li>Build quality backlinks from relevant sources</li>
                <li>Monitor progress with regular SEO audits</li>
              </ol>
            </div>
            
            <div className="mt-4 p-4 border border-desert-sunset/20 bg-desert-sunset/5 rounded-lg">
              <h4 className="font-medium mb-2 text-desert-sunset">Need Expert Help?</h4>
              <p className="text-sm">
                Our team can implement all these recommendations for you with our professional SEO services. We'll develop a customized strategy based on this analysis and your business goals.
              </p>
              <Button className="mt-3 bg-desert-sunset hover:opacity-90 transition-opacity">
                <Link to="/contact" className="flex items-center">
                  Get Professional Assistance
                  <ExternalLink className="ml-2 w-4 h-4" />
                </Link>
              </Button>
            </div>
            
            <Separator className="my-6" />
            
            <div>
              <p className="text-xs text-muted-foreground italic">
                Data Source: {result.dataSource}
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default RecommendationsTab;
